import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import useStyles from "../../../components/common/table/styles";

import { PAYMENT_METHOD_OPTIONS, paymentFormElements } from "./invoiceUntils";
import FormField from "../../../components/common/FormField";
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  addPaymentAction,
  getAllPaymentListAction,
  setAddPaymentResponse,
  setNewPaymentLocal,
} from "../../../store/reducers/paymentSlice";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import Validation from "../../../utils/Validation";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import CustomButton from "../../../components/common/button/CustomButton";
import DeleteModel from "./DeleteModel";
import { formatNumberWithComma } from "../../../hooks/helper";

interface PaymentFormProps {
  selectedInvoice?: any;
  isFormReadyOnly?: any;
  invoiceFormData?: any;
  paymentTableData?: any;
}

const PaymentInvoiceTable: React.FC<PaymentFormProps> = ({
  selectedInvoice,
  isFormReadyOnly,
  invoiceFormData,
  paymentTableData,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tableHeadings = [
    { label: "ID" },
    { label: "Pay Date", align: "left" },
    { label: "Pay Method", align: "left" },
    { label: "Pay Type", align: "left" },
    { label: "Amount (£)", align: "center" },
    { label: "Status", align: "center" },
    { label: "Notes", align: "center" },
    { label: "Action", align: "center" },
  ];

  const {
    payments,
    paymentPaginations,
    newPaymentLocal,
    isAddPaymentResponse,
  } = useSelector((state: any) => state.payment);
  const { invoiceDetails } = useSelector((state: any) => state.invoice);
  const { leadDetails } = useSelector((state: any) => state.lead);
  const { userProfile } = useSelector((state: any) => state?.user);
  useEffect(() => {
    if (selectedInvoice || invoiceDetails?.invoice_number) {
      dispatch(
        getAllPaymentListAction(
          selectedInvoice || invoiceDetails?.invoice_number
        )
      );
    }
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [dispatch, selectedInvoice, isAddPaymentResponse, invoiceDetails]);

  const { paymentMethods } = useSelector((state: any) => state?.quickBook);
  const [isProfessionalSection, setIsProfessionalSection] = useState(
    userProfile?.features?.includes("Professional Financial") || false // Safe fallback
  );

  useEffect(() => {
    setIsProfessionalSection(
      userProfile?.features?.includes("Professional Financial") || false
    ); // Sync profile changes
  }, [userProfile]);

  // Initialize formData state
  const [formData, setFormData] = useState({
    ...newPaymentLocal,
    invoice_number: selectedInvoice || invoiceDetails?.invoice_number, // Set invoice number here
  });

  useEffect(() => {
    if (paymentTableData) {
      setFormData({
        ...formData,
        payment_type: "Full",
        payment_method: "Cash",
        amount: paymentTableData?.quick_pay,
        pay_date: new Date().toISOString(),
        note: "Quick Pay Entry",
        invoice_number: paymentTableData?.invoice_number,
      });
    }
  }, [paymentTableData]);

  useEffect(() => {
    if (isAddPaymentResponse || selectedInvoice || invoiceDetails) {
      dispatch(setNewPaymentLocal(newPaymentLocal));

      setFormData({
        payment_type: "",
        payment_method: "",
        amount: "",
        pay_date: "",
        note: "",
        invoice_number: selectedInvoice || invoiceDetails?.invoice_number, // Ensure invoice number is preserved
      });
      dispatch(setAddPaymentResponse(false));
    }
  }, [isAddPaymentResponse, selectedInvoice, invoiceDetails]);

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    payment_type: formData?.payment_type,
    payment_method: formData?.payment_method,
    amount: formData?.amount,
    pay_date: formData?.pay_date,
    note: formData?.note,
  });

  // if professional → use Redux invoiceTypes, else → dummy
  const dynamicPaymentMethod =
    isProfessionalSection && paymentMethods?.length
      ? paymentMethods.map((item: any) => ({
          id: item.id ?? item.name,
          name: item.name,
          tooltip: item.description || item.tooltip || "", // << ADD TOOLTIP
        }))
      : PAYMENT_METHOD_OPTIONS;

  // -----------------------------
  // 🔹 STEP 3: Inject dynamic options into payment method field
  // -----------------------------
  const formElementsWithDynamicOptions = paymentFormElements.map((field) => {
    if (field.id === "payment_method") {
      return { ...field, options: dynamicPaymentMethod };
    }
    return field;
  });

  const onChangeAction = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    if (id === "payment_method") {
      if (isProfessionalSection) {
        const selectedType = paymentMethods?.find(
          (type: any) => type.name === value || type.id === value
        );
        const updatedFormData = {
          ...formData,
          payment_method: selectedType?.name || value,
          payment_method_id: selectedType?.id || null,
        };
        setFormData(updatedFormData);
        dispatch(
          setNewPaymentLocal({
            ...newPaymentLocal,
            payment_method: selectedType?.name || value,
            payment_method_id: selectedType?.id || null,
          })
        );
      } else {
        const updatedFormData = {
          ...formData,
          payment_method: value,
        };
        setFormData(updatedFormData);
        dispatch(
          setNewPaymentLocal({
            ...newPaymentLocal,
            payment_method: value,
          })
        );
      }
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });

      dispatch(
        setNewPaymentLocal({
          ...newPaymentLocal,
          [id]: value,
        })
      );
    }
    resetValidator(id);
  };

  const [isActionCalled, setIsActionCalled] = useState(false);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (!isError() && error !== null && isActionCalled) {
        dispatch(
          addPaymentAction({
            ...formData,
            invoice_number: selectedInvoice || invoiceDetails?.invoice_number,
          })
        );
      }
      setIsActionCalled(false);
    }
  }, [isActionCalled]);

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  const [deletePayemtModel, setDeletePaymentModel] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  const deletePayment = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setDeletePaymentModel(true);
  };

  const handleCloseModel = () => {
    setDeletePaymentModel(false);
    setSelectedPaymentId(null);
  };

const blinkPaymentMethods = [
  "Blink Direct Debit",
  "Blink Debit/Credit Card",
  "Blink Payment Link",
];

const isBlinkPayment = (...values: (string | undefined | null)[]) => {
  return values.some((value) =>
    blinkPaymentMethods.some(
      (method) =>
        method.trim().toLowerCase() === value?.trim().toLowerCase()
    )
  );
};

  return (
    <>
      <Grid container sx={{ mt: 2 }}>
        <Grid item md={12} xs={12}>
          <Typography
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            Payments On This Invoice
          </Typography>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableHead sx={{ background: "#02906F" }}>
                <TableRow>
                  {tableHeadings &&
                    tableHeadings.map((item, index) => (
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          padding: "4px",
                          color: "#fff",
                          textAlign: "center",
                        }}
                        className={classes.tableHeadCell}
                        key={index}
                      >
                        {item?.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isFormReadyOnly && (
                  <TableRow>
                    <TableCell sx={{ textAlign: "center", padding: "10px" }}>
                      {payments?.length + 1}
                    </TableCell>
                    {formElementsWithDynamicOptions?.map((field, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          padding:
                            field?.id === "pay_date" ? "2px 10px" : "10px",
                          width: field.id === "note" ? "260px" : "180px",
                          alignItems: "center",
                          paddingBottom:
                            field.id === "pay_date" ? "8px" : "auto",
                        }}
                      >
                        <FormField
                          onPaste={(e: any) => e.preventDefault()}
                          onKeyDown={(event: any) => {
                            if (
                              event.keyCode === 8 ||
                              event.keyCode === 9 ||
                              event.keyCode === 37 ||
                              event.keyCode === 38 ||
                              event.keyCode === 39 ||
                              event.keyCode === 40 ||
                              event.keyCode === 18 ||
                              event.keyCode === 17 ||
                              event.ctrlKey
                            )
                              return;

                            if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(event.key)
                            ) {
                              event.preventDefault();
                            } else if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(
                                event.target.value + event.key
                              )
                            ) {
                              event.preventDefault();
                            }
                          }}
                          {...{
                            ...field,
                            // value: newPaymentLocal[field.id]
                            //   ? newPaymentLocal[field.id]
                            //   : "",
                            value:field?.id ==='payment_method' && isProfessionalSection
                            ? formData ? formData?.payment_method_id : ""
                            : formData ? formData[field?.id] : "",
                            variant: "outlined",
                            onChange: onChangeAction,
                            onBlur: onBlurAction,
                            disabled: isBlinkPayment(invoiceFormData?.payment_method),
                            editReadOnly: isBlinkPayment(invoiceFormData?.payment_method),
                            isNonMarginTop: true,
                            InputProps: {
                              ...(field?.inputProps && field?.inputProps),
                              ...{ disabled: field?.readonly },
                              sx: { height: "36px" },
                            },
                            error: error && error[field?.id],
                            //   helperText: error && error[field?.id],
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: "center", padding: "10px" }}>
                      {invoiceFormData?.lead_status !== "COMPLETED" && (
                        <CustomButton
                          style={{ marginRight: "10px" }}
                          title={`Save`}
                          disabled={
                            invoiceFormData?.quick_pay === 0 ||
                            invoiceFormData?.quick_pay === null ||
                            isBlinkPayment(invoiceFormData?.method)
                          }
                          onClick={(e: any) => handleSubmit(e)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {payments
                  ? payments.map((val: any, idx: number) => (
                      <TableRow>
                        <>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "60px",
                              textAlign: "center",
                            }}
                          >
                            {idx + 2}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "160px",
                              textAlign: "center",
                            }}
                          >
                            {dayjs(val?.pay_date).format(
                              userProfile?.dateFormat
                                ? userProfile?.dateFormat
                                : "DD/MM/YYYY"
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "160px",
                              textAlign: "center",
                            }}
                          >
                            {val?.payment_method}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "160px",
                              textAlign: "center",
                            }}
                          >
                            {val?.payment_type}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "160px",
                              textAlign: "center",
                            }}
                          >
                            £ {formatNumberWithComma(val?.amount)}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "160px",
                              textAlign: "center",
                            }}
                          >
                            {val?.status}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "auto",
                            }}
                          >
                            {val?.note}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "10px",
                              width: "auto",
                              justifyContent: "center",
                              display: "flex",
                            }}
                          >
                            {(invoiceFormData?.lead_status !== "COMPLETED" &&
                              !val?.is_advance) && (
                                <CustomButton
                                  title="Undo"
                                  disabled={
                                    isBlinkPayment(val?.payment_method) ||
                                    (invoiceFormData?.invoice_type ===
                                      "deposit" &&
                                    leadDetails?.lead?.status === "JOB")
                                      ? true
                                      : false
                                  }
                                  variant="outlined"
                                  onClick={() => {
                                    deletePayment(val?.payment_id);
                                  }}
                                />
                              )}
                          </TableCell>
                        </>
                      </TableRow>
                    ))
                  : ""}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      {deletePayemtModel && (
        <DeleteModel
          open={deletePayemtModel}
          onHide={() => handleCloseModel()}
          selectedPaymentId={selectedPaymentId}
          alertModalShow={deletePayemtModel}
          setAlertModelShow={setDeletePaymentModel}
        />
      )}
    </>
  );
};

export default PaymentInvoiceTable;
